import React, { FC, useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ProjectPageShell, { Crumb } from "../shared/ProjectPageShell";
import IssueInDetailView from "./IssueInDetailView";
import { RootState } from "../../../store/store";
import {
  updateIssueInDetailModalSlice,
  resetIssueInDetailModalSlice,
} from "../../../store/slices/projectManagement/issuee-view/IssueInDetailView";
import { issueApis } from "../../../api/Issue";

const IssueDetailPage: FC = () => {
  const { issueId } = useParams<{ issueId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [issueData, setIssueData] = useState<any>(null);

  // Get data from location state
  const locationState = location.state as any;
  const projectId = locationState?.projectId || locationState?.id;
  const projectName = locationState?.projectName;
  const fromWorkApprovals = locationState?.fromWorkApprovals;
  const fromNotifications = locationState?.fromNotifications;
  // Generic return path - allows navigation back to any source page
  const returnPath = locationState?.returnPath;
  const returnState = locationState?.returnState;

  const { data: IssueInDetailModal } = useSelector(
    (state: RootState) => state.IssueListInDetailModal
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const resolvedProjectId = projectId || currentProjectId;
  const resolvedProjectName = projectName || currentProjectName;

  // Fetch issue details (reusable so children can call it as refreshData)
  const fetchIssueDetails = useCallback(
    async (showSpinner: boolean = true) => {
      if (!issueId) return;
      if (showSpinner) setLoading(true);
      try {
        const response = await issueApis.fetchIssueById(
          Number(issueId),
          "Active"
        );
        if (response?.status && response?.data) {
          const issue = response.data;
          setIssueData(issue);

          dispatch(
            updateIssueInDetailModalSlice({
              open: false,
              id: issue.issueId,
              issueId: issue.issueId,
              issueName: issue.issueName,
              projectId: issue.projectId || resolvedProjectId,
              projectName:
                resolvedProjectName || issue.projectTitle || "Project",
              selectedIssueDetails: issue,
              activeTab: "view",
              extra: {
                severity: issue.priorityName,
                assignee: issue.IssueAssignedToName,
                reporter: issue.IssueAssignedByName,
                tags: issue.issueTags?.map((t: any) => t.tagName),
                deadlineDate: issue.deadlineDate,
              },
            })
          );
        }
      } catch (error) {
        console.error("Error fetching issue details:", error);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [issueId, dispatch, resolvedProjectId, resolvedProjectName]
  );

  useEffect(() => {
    fetchIssueDetails();
  }, [fetchIssueDetails]);

  useEffect(() => {
    return () => {
      dispatch(resetIssueInDetailModalSlice());
    };
  }, [dispatch]);

  // Navigation handlers
  const handleBack = () => {
    dispatch(resetIssueInDetailModalSlice());
    // Use returnPath if available (e.g., from notifications)
    if (returnPath) {
      navigate(returnPath, { state: returnState });
    } else if (fromWorkApprovals) {
      navigate(`/pms/project/workapprovals/${resolvedProjectId}`, {
        state: {
          id: resolvedProjectId,
          projectName: resolvedProjectName,
        },
      });
    } else {
      navigate(`/pms/project/issues/${resolvedProjectId}`, {
        state: {
          ...locationState,
          id: resolvedProjectId,
          projectName: resolvedProjectName,
        },
      });
    }
  };

  const handleBackToIssues = () => {
    dispatch(resetIssueInDetailModalSlice());
    navigate(`/pms/project/issues/${resolvedProjectId}`, {
      state: {
        ...locationState,
        id: resolvedProjectId,
        projectName: resolvedProjectName,
      },
    });
  };

  const handleBackToProject = () => {
    dispatch(resetIssueInDetailModalSlice());
    navigate(`/pms/project/dashboard/${resolvedProjectId}`, { state: locationState });
  };

  const handleClose = () => {
    handleBackToIssues();
  };

  const issueName =
    issueData?.issueName ||
    IssueInDetailModal?.selectedIssueDetails?.issueName ||
    "Issue";

  const breadcrumb: Crumb[] = [
    { label: resolvedProjectName || "Project", onClick: handleBackToProject },
    { label: "Issues", onClick: handleBackToIssues },
    { label: issueName },
  ];

  return (
    <ProjectPageShell
      headTitle={`${issueName} - Issue Details`}
      breadcrumb={breadcrumb}
      title={issueName}
      eyebrow="Issue"
      onBack={handleBack}
      loading={loading}
    >
      <IssueInDetailView
        id={Number(issueId)}
        activeTab={locationState?.activeTab || "view"}
        refreshData={() => fetchIssueDetails(false)}
        handleClose={handleClose}
      />
    </ProjectPageShell>
  );
};

export default IssueDetailPage;
